import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Modal,
  Alert,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TipoChavePix, ChavePix } from '@/types/app';
import { Phone, Mail, User, Key, X } from 'lucide-react-native';

interface ChavePixSelectorProps {
  value?: ChavePix;
  onChange: (chavePix: ChavePix | undefined) => void;
  placeholder?: string;
}

const TIPOS_CHAVE: { tipo: TipoChavePix; label: string; icon: any; placeholder: string }[] = [
  { tipo: 'telefone', label: 'Telefone', icon: Phone, placeholder: '(11) 99999-9999' },
  { tipo: 'email', label: 'E-mail', icon: Mail, placeholder: 'seu@email.com' },
  { tipo: 'cpf', label: 'CPF', icon: User, placeholder: '123.456.789-00' },
  { tipo: 'aleatoria', label: 'Chave Aleatória', icon: Key, placeholder: 'chave-aleatoria-123' },
];

export default function ChavePixSelector({ value, onChange, placeholder }: ChavePixSelectorProps) {
  const { colors } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState<TipoChavePix | null>(value?.tipo || null);
  const [valorChave, setValorChave] = useState(value?.valor || '');

  const handleSalvar = () => {
    if (!tipoSelecionado || !valorChave.trim()) {
      Alert.alert('Erro', 'Selecione o tipo e digite a chave PIX');
      return;
    }

    onChange({
      tipo: tipoSelecionado,
      valor: valorChave.trim()
    });
    setShowModal(false);
  };

  const handleLimpar = () => {
    onChange(undefined);
    setTipoSelecionado(null);
    setValorChave('');
    setShowModal(false);
  };

  const getTipoInfo = (tipo: TipoChavePix) => {
    return TIPOS_CHAVE.find(t => t.tipo === tipo);
  };

  const styles = StyleSheet.create({
    container: {
      marginBottom: 16,
    },
    label: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      marginBottom: 8,
    },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    buttonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    buttonText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      marginLeft: 12,
      flex: 1,
    },
    placeholderText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginLeft: 12,
      flex: 1,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalContent: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 24,
      width: '90%',
      maxWidth: 400,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    closeButton: {
      padding: 4,
    },
    tipoSection: {
      marginBottom: 24,
    },
    tipoTitle: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      marginBottom: 12,
    },
    tipoOptions: {
      gap: 8,
    },
    tipoOption: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    tipoOptionSelected: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    tipoOptionText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      marginLeft: 12,
    },
    tipoOptionTextSelected: {
      color: colors.primary,
    },
    inputSection: {
      marginBottom: 24,
    },
    inputTitle: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      marginBottom: 8,
    },
    input: {
      backgroundColor: colors.background,
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    buttonRow: {
      flexDirection: 'row',
      gap: 12,
    },
    buttonSecondary: {
      flex: 1,
      backgroundColor: colors.border,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    buttonPrimary: {
      flex: 1,
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    buttonTextPrimary: {
      color: '#FFFFFF',
    },
  });

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.button} onPress={() => setShowModal(true)}>
        <View style={styles.buttonContent}>
          {value ? (
            <>
              {React.createElement(getTipoInfo(value.tipo)?.icon, { 
                size: 20, 
                color: colors.textSecondary 
              })}
              <Text style={styles.buttonText}>{value.valor}</Text>
            </>
          ) : (
            <>
              <Key size={20} color={colors.textSecondary} />
              <Text style={styles.placeholderText}>
                {placeholder || 'Selecione o tipo e digite a chave PIX'}
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>

      <Modal visible={showModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Configurar Chave PIX</Text>
              <TouchableOpacity style={styles.closeButton} onPress={() => setShowModal(false)}>
                <X size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <View style={styles.tipoSection}>
              <Text style={styles.tipoTitle}>Tipo de Chave</Text>
              <View style={styles.tipoOptions}>
                {TIPOS_CHAVE.map((tipo) => {
                  const Icon = tipo.icon;
                  const isSelected = tipoSelecionado === tipo.tipo;
                  return (
                    <TouchableOpacity
                      key={tipo.tipo}
                      style={[styles.tipoOption, isSelected && styles.tipoOptionSelected]}
                      onPress={() => setTipoSelecionado(tipo.tipo)}
                    >
                      <Icon size={20} color={isSelected ? colors.primary : colors.textSecondary} />
                      <Text style={[
                        styles.tipoOptionText,
                        isSelected && styles.tipoOptionTextSelected
                      ]}>
                        {tipo.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {tipoSelecionado && (
              <View style={styles.inputSection}>
                <Text style={styles.inputTitle}>Valor da Chave</Text>
                <TextInput
                  style={styles.input}
                  value={valorChave}
                  onChangeText={setValorChave}
                  placeholder={getTipoInfo(tipoSelecionado)?.placeholder}
                  placeholderTextColor={colors.textSecondary}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
            )}

            <View style={styles.buttonRow}>
              <TouchableOpacity style={styles.buttonSecondary} onPress={handleLimpar}>
                <Text style={styles.buttonText}>Limpar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.buttonPrimary} onPress={handleSalvar}>
                <Text style={[styles.buttonText, styles.buttonTextPrimary]}>Salvar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}